import React, { useEffect, useMemo, useState } from 'react';
import { Datatable } from '../Datatable/Datatable';
import { Dropdown } from 'react-bootstrap'

// Interfaces para tipagem
interface Product {
    id: string
    name: string
    price: string
    image: string
    sellerId: string
    allowDiscount: boolean
    sellerCatalog: boolean
    selected: boolean
}

interface TableHeader {
    key: string;
    value: string;
    type: string;
    noOrder?: boolean;
    action?: (item: any) => void;
    show?: boolean;
}

interface TableOption {
    icon: string;
    title: string;
    color: string;
    action: (item: any) => void;
}

interface CatalogOption {
    name: string;
    icon: string;
    action: () => void;
    show?: boolean;
}

interface CatalogProductsTableProps {
    // Props principais
    products: Product[];
    selectedCount: number;
    showSellerOptions?: boolean;

    // Props de ações
    onSelectAll: (all: boolean, id: number, value: boolean) => void;
    onRemoveProduct: (productId: string) => void;
    onToggleDiscount?: (productId: number, currentStatus: boolean) => void;

    // Props de ações em massa
    onAddMultipleDiscount?: () => void;
    onRemoveMultipleDiscount?: () => void;
    onRemoveMultipleProducts?: () => void;

    // Props de customização
    title?: string;
    titleIcon?: string;
    emptyMessage?: string;
    emptySubMessage?: string;
    tableColor?: string;
    maxTableHeight?: string;
    searchPlaceholder?: string;

    // Props opcionais de header customizado
    customHeaders?: TableHeader[];
    customOptions?: TableOption[];
    catalogOptions?: CatalogOption[];

    // Props de layout
    cardClassName?: string;
    showCatalogOptions?: boolean;
}

const CatalogProductsTable: React.FC<CatalogProductsTableProps> = ({
    // Props principais
    products = [],
    selectedCount = 0,
    showSellerOptions = false,

    // Props de ações
    onSelectAll,
    onRemoveProduct,
    onToggleDiscount,

    // Props de ações em massa
    onAddMultipleDiscount,
    onRemoveMultipleDiscount,
    onRemoveMultipleProducts,

    // Props de customização
    title = 'Produtos no Catálogo',
    titleIcon = 'fas fa-clipboard',
    emptyMessage = 'Nenhum produto adicionado ao catálogo',
    emptySubMessage = 'Use a pesquisa ao lado para encontrar e adicionar produtos',
    tableColor = 'success',
    maxTableHeight = '400px',
    searchPlaceholder = 'Procurar produtos no catálogo',

    // Props opcionais
    customHeaders,
    customOptions,
    catalogOptions,

    // Props de layout
    cardClassName = 'card col-6 mb-5',
    showCatalogOptions = true,
}) => {

    // Headers padrão da tabela
    const defaultHeaders: TableHeader[] = [
        { key: 'Código', value: 'code', type: 'default' },
        { key: 'Produto', value: 'name', type: 'default' },
        {
            key: 'Permitir Desconto',
            value: 'allowDiscount',
            type: 'check',
            action: (item: any) => onToggleDiscount?.(item.id, item.allowDiscount),
            show: showSellerOptions,
        },
        { key: 'Ações', value: 'acoes', type: '', noOrder: true },
    ];

    // Opções padrão da tabela
    const defaultOptions: TableOption[] = [
        {
            icon: 'fas fa-trash',
            action: (item: any) => onRemoveProduct(item.id),
            title: 'remover',
            color: 'danger',
        },
    ];

    // Opções padrão do catálogo
    const defaultCatalogOptions: CatalogOption[] = [
        {
            name: 'Adicionar Desconto Produto',
            icon: 'bi-plus',
            action: () => onAddMultipleDiscount?.(),
            show: showSellerOptions,
        },
        {
            name: 'Remover Desconto de Produto',
            icon: 'bi-dash',
            action: () => onRemoveMultipleDiscount?.(),
            show: showSellerOptions,
        },
        {
            name: 'Remover Produto(s)',
            icon: 'bi-trash',
            action: () => onRemoveMultipleProducts?.(),
        },
    ];

    // Usar headers customizados ou padrão
    const tableHeaders = customHeaders || defaultHeaders;
    const tableOptions = customOptions || defaultOptions;
    const catalogOptionsToShow = catalogOptions || defaultCatalogOptions;

    const [searchInput, setSearchInput] = useState<string>('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>('');

    // Função para limpar a pesquisa
    const handleClearSearch = () => {
        setSearchInput('');
    }

    // Debounce do valor de pesquisa
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchValue(searchInput);
        }, 500); // 500ms de delay

        return () => clearTimeout(timer);
    }, [searchInput]);

    const filteredProducts = useMemo(() => {
        if (!debouncedSearchValue) return products;

        return products.filter((product: Product) =>
            product.name.toLowerCase().includes(debouncedSearchValue.toLowerCase()) ||
            (product as any).code?.toLowerCase().includes(debouncedSearchValue.toLowerCase())
        );
    }, [products, debouncedSearchValue]);


    return (
        <div className={cardClassName}>
            <div className='card-header py-5'>
                <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bolder fs-3 mb-1'>
                        <i className={`${titleIcon} me-2 text-info`}></i>
                        {title}
                    </span>
                </h3>
                <div className='col-md-4'>
                    <div className='form-group'>
                        <div className='d-flex gap-2'>
                            <button
                                className={`btn  w-100`}
                                type='button'
                                onClick={() => console.log('Show Filters')} // Função para mostrar filtros
                                disabled
                            >
                                    <i className={` `} />
                            </button>
                         
                        </div>
                    </div>
                </div>
            </div>

            <div className='card-body h-100'>
                <div className='row '>
                    {/* Opções do catálogo (ações em massa) */}
                    <div className={`card-header px-0 pb-8  ${products.length === 0 && 'd-none' }`}>
                        <div className={`form-group col-7`}>
                            <div className='input-group'>
                                <input
                                    type='text'
                                    className='form-control fw-normal'
                                    style={{ backgroundColor: '#f3f6f9' }}
                                    placeholder={searchPlaceholder}
                                    value={searchInput} // Usar searchInput aqui
                                    onChange={e => setSearchInput(e.target.value)} // Atualizar searchInput
                                />
                                <button
                                    className='btn btn-primary'
                                    type='button'
                                    onClick={() => console.log('')} // Usar searchInput para pesquisa

                                >
                                    <i className='fas fa-search'></i>
                                </button>
                                {searchInput && ( // Verificar searchInput para mostrar botão
                                    <button
                                        className='btn btn-outline-danger'
                                        type='button'
                                        onClick={handleClearSearch}
                                    >
                                        <i className='fas fa-times'></i>
                                    </button>
                                )}
                            </div>

                        </div>



                        {showCatalogOptions && selectedCount > 0 && (
                            <div className='col-5 d-flex justify-content-end align-items-center h-full'>
                                <Dropdown
                                    className='btn btn-primary btn-sm fw-bolder me-5 p-0 '
                                    style={{ marginLeft: '1em' }}
                                >
                                    <Dropdown.Toggle variant='default' size='sm'>
                                        <i className='bi bi-three-dots fs-4 p-0'></i>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className='p-0'>
                                        {catalogOptionsToShow.map((option, index) => (
                                            option.show !== false && (
                                                <Dropdown.Item
                                                    key={index}
                                                    className='d-flex align-items-center'
                                                    onClick={option.action}
                                                >
                                                    <div
                                                        className='btn m-2 d-flex flex-row justify-content-center align-items-center'
                                                        style={{ color: '#6993FF', backgroundColor: '#E1E9FF', width: '90%' }}
                                                    >
                                                        <i className={`${option.icon}`}
                                                            style={{ color: '#6993FF', fontSize: '1.5rem', marginRight: '0.5em' }}></i>

                                                        <p style={{ color: '#6993FF', fontSize: '1rem' }} className='m-0'>
                                                            {option.name}
                                                        </p>
                                                    </div>

                                                </Dropdown.Item>
                                            )
                                        ))}


                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        )}
                    </div>

                    {/* Tabela de Produtos do Catálogo */}
                    <div className='col-12'>
                        <div className='card'>
                            <div className='card-body p-3'>
                                {products.length > 0 ? (
                                    <Datatable
                                        data={filteredProducts}
                                        headers={tableHeaders}
                                        selectable={true}
                                        showControls={false}
                                        onSelectable={onSelectAll}
                                        options={tableOptions}
                                        color={tableColor}
                                        headerFixed
                                        maxHeight={maxTableHeight}
                                    />
                                ) : (
                                    <div className='text-center p-5'>
                                        <i className='fas fa-list fa-3x text-muted mb-3'></i>
                                        <p className='text-muted'>{emptyMessage}</p>
                                        <small className='text-muted'>{emptySubMessage}</small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default CatalogProductsTable;